import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import InfoTooltip from './InfoTooltip';
import { PriceReqs, ZayavkaReqs, ProjectReqs } from './FileRequirements';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Куда вернуть после входа: путь из ?next=..., иначе дашборд. Только внутренние пути.
  const nextRaw = new URLSearchParams(location.search).get('next') || '';
  const from = nextRaw.startsWith('/') && !nextRaw.startsWith('//') ? nextRaw : '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (isRegisterMode) {
        await apiService.register({ email, password });
        toast.success('Регистрация прошла успешно! Теперь войдите.');
        setIsRegisterMode(false);
      } else {
        await apiService.login({ username: email, password });
        toast.success('Вход выполнен успешно!');
        navigate(from, { replace: true });
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.detail ||
        (isRegisterMode ? 'Ошибка регистрации' : 'Ошибка входа')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50">
      {/* ЛЕВАЯ ЧАСТЬ — брендовый герой */}
      <div className="relative lg:w-[56%] overflow-hidden bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 text-white px-6 py-10 sm:px-10 lg:px-14 lg:py-14 flex flex-col">
        {/* декоративное свечение */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-24 h-80 w-80 rounded-full bg-teal-400/10 blur-3xl" />

        <div className="relative flex-1 flex flex-col max-w-xl">
          {/* Бренд */}
          <div className="flex items-center gap-2 mb-10">
            <span className="text-2xl font-extrabold tracking-tight">ИИсчёт</span>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-emerald-300 bg-emerald-400/10 ring-1 ring-emerald-400/30 rounded-full px-2 py-0.5">
              первые тесты
            </span>
          </div>

          {/* Заголовок */}
          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold leading-tight tracking-tight">
            Автоматическая обработка заявок
            <span className="text-emerald-400"> и формирование счёта</span>
          </h1>
          <p className="mt-4 text-emerald-100/80 text-base sm:text-lg leading-relaxed">
            Сервис для поставщиков стройматериалов на базе ИИ. Загрузите свой прайс и заявку —
            ИИсчёт сопоставит позиции, проверит единицы измерения, подберёт аналоги и сформирует
            готовый счёт.
          </p>

          {/* Две вещи для старта */}
          <div className="mt-9">
            <p className="text-sm font-semibold uppercase tracking-wider text-emerald-300/90 mb-3">
              Для работы нужны всего две вещи
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300 font-bold">1</div>
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold text-white">Прайс или выгрузка номенклатуры</p>
                  <p className="text-emerald-100/70">
                    Ваш каталог с ценами.{' '}
                    <InfoTooltip label="требования к файлу" triggerClassName="text-emerald-300 hover:text-emerald-200">
                      <PriceReqs />
                    </InfoTooltip>
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-xl bg-white/5 ring-1 ring-white/10 p-4">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300 font-bold">2</div>
                <div className="text-sm leading-relaxed">
                  <p className="font-semibold text-white">Заявки или рабочая документация</p>
                  <p className="text-emerald-100/70">
                    То, что нужно обсчитать:{' '}
                    <InfoTooltip label="заявка (Excel)" triggerClassName="text-emerald-300 hover:text-emerald-200">
                      <ZayavkaReqs />
                    </InfoTooltip>
                    {' '}или{' '}
                    <InfoTooltip label="проект (PDF)" triggerClassName="text-emerald-300 hover:text-emerald-200">
                      <ProjectReqs />
                    </InfoTooltip>
                  </p>
                </div>
              </div>
            </div>

            <p className="mt-4 text-xs text-emerald-100/60 leading-relaxed">
              Заявку принимаем в любом виде: Excel, CSV, PDF, Word или фотографию — переделывать
              файл под форму не нужно. Что мы поняли из файла, вы увидите и сможете поправить
              до расчёта. Строгие требования остались только к прайсу.
            </p>
          </div>

          {/* Поддержка */}
          <div className="mt-auto pt-8">
            <p className="text-xs text-emerald-100/60 leading-relaxed">
              Что-то не загружается дольше 20 минут или ошибка? Напишите нам —
              ответим и всё поправим:
            </p>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm">
              <a href="mailto:mikirich@ya.ru" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">mikirich@ya.ru</a>
              <a href="tel:+79120500500" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">+7 912 050-05-00</a>
              <a href="https://t.me/kirichmi" target="_blank" rel="noreferrer" className="text-emerald-300 hover:text-emerald-200 underline underline-offset-2">Telegram @kirichmi</a>
            </div>
          </div>
        </div>
      </div>

      {/* ПРАВАЯ ЧАСТЬ — форма входа/регистрации */}
      <div className="lg:w-[44%] flex items-center justify-center px-6 py-12 sm:px-10">
        <div className="w-full max-w-md">
          <h2 className="text-2xl font-bold text-gray-900">
            {isRegisterMode ? 'Создать аккаунт' : 'Вход в систему'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isRegisterMode
              ? 'Зарегистрируйтесь, чтобы начать тестировать ИИсчёт.'
              : 'Рады видеть снова. Войдите в свой кабинет.'}
          </p>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.ru"
                className="block w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 focus:outline-none"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Пароль</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete={isRegisterMode ? 'new-password' : 'current-password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="block w-full rounded-lg border border-gray-300 px-3.5 py-2.5 text-sm placeholder-gray-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 focus:outline-none"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 transition-colors"
            >
              {isLoading ? 'Подождите…' : (isRegisterMode ? 'Зарегистрироваться' : 'Войти')}
            </button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-gray-50 text-gray-400">
                {isRegisterMode ? 'уже есть аккаунт?' : 'ещё нет аккаунта?'}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsRegisterMode(!isRegisterMode)}
            className="w-full flex justify-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 transition-colors"
          >
            {isRegisterMode ? 'Войти в существующий' : 'Зарегистрироваться'}
          </button>

          <p className="mt-6 text-center text-xs text-gray-400">
            Регистрируясь, вы принимаете{' '}
            <a href="https://iishet.ru/oferta/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">публичную оферту</a>{' '}
            и{' '}
            <a href="https://iishet.ru/privacy/" target="_blank" rel="noreferrer" className="underline hover:text-gray-600">политику обработки данных</a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
